"use client"

import { React, useState, useEffect, useCallback, useMemo, useRef } from "react"
import { Redirect, useLocation, useParams } from "react-router-dom"
import useGroupWorkspaceAccess from "../../hooks/DataFetching/useGroupWorkspaceAccess"
import { FetchActions } from "../FetchActions"
import { FetchCampaigns } from "../FetchCampaigns"
import { PostCampaign } from "../PostCampaign"
import GroupBannerEditModal from "./GroupBannerEditModal"
import { bannerImageStyle, parseFraming } from "./bannerFraming"
import TreasurerNotice from "./TreasurerNotice"
import "@fortawesome/fontawesome-free/css/all.css"
import "../../styles/components/GroupMembersPage.css"
import { useAuth0 } from "@auth0/auth0-react"
import GroupForums from "./GroupForums"
import { useGroupPositions } from "../../hooks/DataFetching/useGroupPositions"
import { useGroupWorkspaceCounts } from "../../hooks/DataFetching/useGroupWorkspaceCounts"
import { JobModal } from "../Marketplace/Jobs/JobModal"
import { ApplicationsModal } from "../Marketplace/Jobs/ApplicationsModal"
import { usePositionApplications } from "../../hooks/DataFetching/usePositionApplications"
import TaskBoard from "./Tasks/TaskBoard"
import { FetchProblems } from "../FetchProblems"
import { PostProblem } from "../PostProblem"
import { FetchConflicts } from "../FetchConflicts"
import { PostConflict } from "../PostConflict"
import GroupPolicies from "./GroupPolicies"
import RefinementList from "./RefinementList"
import GroupCalendar from "./GroupCalendar"
import FellowshipMeetLauncher from "./FellowshipMeetLauncher"
import FellowshipPanel from "./FellowshipPanel"
import GroupBtcStatement from "./GroupBtcStatement"
import ChurchInvitePanel from "./ChurchInvitePanel"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags"
import { getApiBaseUrl } from "../../config/apiBase"
import { StatusMessage } from "../ui/StatusMessage"
import {
  Activity,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  ClipboardList,
  FileText,
  Flag,
  Gavel,
  Lightbulb,
  Network,
  Search,
  MessageCircle,
  Pencil,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react"

const formatBtcAmount = (value) => {
  const numericValue = Number(value || 0)
  return `${numericValue.toFixed(8)} BTC`
}

const formatDateTime = (value) => {
  if (!value) {
    return "Not scheduled"
  }

  return new Date(value).toLocaleString()
}

const payrollWarningLabels = {
  TooManyScheduledAutopayments: "High autopayment volume",
  MissingRecipientWallets: "Recipient wallet issue",
  MissingRecipientWallet: "Recipient wallet issue",
  PayrollDueNow: "Payroll due now",
  PayrollRequiresRecovery: "Payroll recovery needed",
  PayrollFailed: "Payroll failed",
  InsufficientGroupFunds: "Group treasury short",
}

const payrollWarningActions = {
  TooManyScheduledAutopayments: "Review scheduled job payroll before approving more paid positions.",
  MissingRecipientWallets: "Ask affected workers to restore or create a BTC wallet before the next payroll run.",
  MissingRecipientWallet: "Recipient wallet must be active before this payroll can be paid.",
  PayrollDueNow: "This payroll is due in the current window.",
  PayrollRequiresRecovery: "A failed or stalled payroll intent needs review before it can be retried safely.",
  PayrollFailed: "This payroll failed and remains visible until it is recovered or cancelled.",
  InsufficientGroupFunds: "The group treasury did not have enough BTC for at least one payroll attempt.",
}

const getPayrollWarningLabel = (warning) => payrollWarningLabels[warning] || warning

const getPayrollWarningAction = (warning) => payrollWarningActions[warning] || "Review this payroll item before the next payroll run."

const TAB_META = {
  forums: { label: "Forums", icon: MessageCircle },
  problems: { label: "Needs", icon: Flag },
  ideas: { label: "Ideas", icon: Lightbulb },
  refining: { label: "Refining", icon: Sparkles },
  actions: { label: "Actions", icon: Activity },
  taskboard: { label: "Taskboard", icon: ClipboardList },
  calendar: { label: "Calendar", icon: CalendarDays },
  roles: { label: "Roles", icon: BadgeCheck },
  church: { label: "Church", icon: Network },
  positions: { label: "Positions", icon: BriefcaseBusiness },
  policies: { label: "Policies", icon: ScrollText },
}

const formatTabLabel = (tab) => TAB_META[tab]?.label || tab.charAt(0).toUpperCase() + tab.slice(1)

const isGeographicGroup = (group) => {
  const groupType = String(group?.groupType || group?.GroupType || "").trim().toLowerCase()
  return groupType === "geographic" || groupType === "geo"
}

const isChurchGroup = (group) => {
  const groupType = String(group?.groupType || group?.GroupType || "").trim().toLowerCase()
  return groupType === "congregation" || groupType === "church" || groupType === "parish"
}

const isChurchNetworkGroup = (group) => {
  const groupType = String(group?.groupType || group?.GroupType || "").trim().toLowerCase()
  return isChurchGroup(group) || groupType === "denomination" || groupType === "fellowship" || groupType === "network"
}

const formatEnrollmentPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "Estimate needed"
  }

  return `${Number(value).toFixed(1)}%`
}

const GroupMembersPage = (props) => {
  const { groupId } = useParams()
  const access = useGroupWorkspaceAccess(groupId)
  if (access === "public") return <Redirect to={`/group/${groupId}`} />
  if (access === "missing") return <p role="alert">This group could not be found.</p>
  if (access === "error") return <p role="alert">Could not check your membership. Please refresh to try again.</p>
  if (access !== "allowed") return <p role="status">Checking group membership…</p>
  return <GroupMemberWorkspace key={groupId} {...props} />
}

const GroupMemberWorkspace = ({ isDarkMode }) => {
  const { groupId } = useParams()
  const location = useLocation()
  const [group, setGroup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState("")
  const [members, setMembers] = useState([])
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState("")
  const [groupRoles, setGroupRoles] = useState([])
  const [rolesLoading, setRolesLoading] = useState(false)
  const [rolesError, setRolesError] = useState("")
  const [memberSearch, setMemberSearch] = useState("")
  const [bannerImageUrl, setBannerImageUrl] = useState("")
  const [modalVisible, setBannerModalVisible] = useState(false)
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth0()
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently)
  const apiUrl = getApiBaseUrl()
  const [activeTab, setActiveTab] = useState("forums")
  const campaignVotingEnabled = isFeatureEnabled(FEATURE_FLAGS.campaignVoting)
  const jobsPayrollEnabled = isFeatureEnabled(FEATURE_FLAGS.jobsPayroll)
  const bitcoinTransactionsEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience)
  const groupFinanceEnabled = isFeatureEnabled(FEATURE_FLAGS.groupStripeFinance) || isFeatureEnabled(FEATURE_FLAGS.cashCheckStewardship)
  const { positions, loading: positionsLoading, error: positionsError } = useGroupPositions(groupId, jobsPayrollEnabled)
  const [payrollObligations, setPayrollObligations] = useState(null)
  const [payrollLoading, setPayrollLoading] = useState(false)
  const [payrollError, setPayrollError] = useState("")
  const [churchInsights, setChurchInsights] = useState(null)
  const [churchInsightsLoading, setChurchInsightsLoading] = useState(false)
  const [churchInsightsError, setChurchInsightsError] = useState("")
  const [selectedPosition, setSelectedPosition] = useState(null)
  const [selectedPositionId, setSelectedPositionId] = useState(null)
  const [problemRefreshKey, setProblemRefreshKey] = useState(0)
  const [workspaceRefreshKey, setWorkspaceRefreshKey] = useState(0)
  const { counts: workspaceCounts, refresh: refreshWorkspaceCounts } = useGroupWorkspaceCounts(groupId, workspaceRefreshKey)
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0)
  const {
    applications,
    loading: applicationsLoading,
    error: applicationsError,
  } = usePositionApplications(selectedPositionId)

  // Main tabs and governance tabs
  const mainTabs = useMemo(() => [
    "forums",
    "problems",
    ...(campaignVotingEnabled ? ["ideas"] : []),
    "refining",
    "actions",
    "taskboard",
    "calendar",
    "roles",
    ...(isChurchNetworkGroup(group) ? ["church"] : []),
  ], [campaignVotingEnabled, group])
  const governanceTabs = useMemo(() => [...(jobsPayrollEnabled ? ["positions"] : []), "policies"], [jobsPayrollEnabled])
  const searchParams = new URLSearchParams(location.search)
  const requestedTab = searchParams.get("tab")
  const requestedCampaignId = searchParams.get("campaignId")
  const requestedActionId = searchParams.get("actionId")
  const requestedCalendarEventId = searchParams.get("calendarEventId")

  useEffect(() => {
    getAccessTokenSilentlyRef.current = getAccessTokenSilently
  }, [getAccessTokenSilently])

  useEffect(() => {
    const availableTabs = [...mainTabs, ...governanceTabs]
    if (requestedTab && availableTabs.includes(requestedTab)) {
      setActiveTab(requestedTab)
    }
  }, [requestedTab, governanceTabs, mainTabs])

  useEffect(() => {
    if (requestedCampaignId && campaignVotingEnabled) setActiveTab("ideas")
  }, [requestedCampaignId, campaignVotingEnabled])

  useEffect(() => {
    if (requestedActionId) setActiveTab("actions")
    if (requestedCalendarEventId) setActiveTab("calendar")
  }, [requestedActionId, requestedCalendarEventId])

  useEffect(() => {
    refreshWorkspaceCounts()
  }, [activeTab, refreshWorkspaceCounts])

  const handleOpenModal = () => {
    setBannerModalVisible(true)
  }

  const handleCloseModal = () => {
    setBannerModalVisible(false)
  }

  const fetchGroupData = useCallback(async () => {
    if (authLoading) {
      return
    }

    if (isAuthenticated === false) {
      setLoading(false)
      setLoadError("Sign in to view this group workspace.")
      return
    }

    setLoading(true)
    setLoadError("")

    try {
      const token = await getAccessTokenSilentlyRef.current({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(`${apiUrl}/api/GroupsTbls/${groupId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setGroup(data)
        setBannerImageUrl(data.bannerUrl)
      } else {
        setLoadError("Could not load this group. Please retry or choose another community.")
      }
    } catch (error) {
      console.error("Error fetching group data:", error)
      setLoadError("Could not load this group. Please retry or choose another community.")
    } finally {
      setLoading(false)
    }
  }, [apiUrl, authLoading, groupId, isAuthenticated])

  useEffect(() => {
    fetchGroupData()
  }, [fetchGroupData])

  const fetchMembers = useCallback(async () => {
    if (!group) {
      setMembers([])
      setMembersError("")
      setMembersLoading(false)
      return
    }

    setMembersLoading(true)
    setMembersLoaded(false)
    setMembersError("")

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(`${apiUrl}/api/GroupsTbls/${groupId}/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Could not load this group's members.")
      }

      const data = await response.json()
      setMembers(Array.isArray(data) ? data : [])
      setMembersLoaded(true)
    } catch (error) {
      console.error("Error fetching group members:", error)
      setMembers([])
      setMembersLoaded(false)
      setMembersError("Member directory is unavailable right now.")
    } finally {
      setMembersLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, group, groupId])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const fetchGroupRoles = useCallback(async () => {
    if (!group) return
    setRolesLoading(true)
    setRolesError("")
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      })
      const response = await fetch(`${apiUrl}/api/GroupsTbls/${groupId}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) throw new Error("Could not load this group's roles.")
      const data = await response.json()
      setGroupRoles(Array.isArray(data) ? data : [])
    } catch (error) {
      setGroupRoles([])
      setRolesError(error.message || "Official roles are unavailable right now.")
    } finally {
      setRolesLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, group, groupId])

  useEffect(() => {
    fetchGroupRoles()
  }, [fetchGroupRoles])

  const rolesByMember = useMemo(() => groupRoles.reduce((index, role) => {
    if (role.memberId && String(role.status).toLowerCase() === "filled") {
      index[role.memberId] = [...(index[role.memberId] || []), role.title]
    }
    return index
  }, {}), [groupRoles])

  const visibleMembers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase()
    if (!query) {
      return members
    }

    return members.filter((member) => (
      String(member.displayName || "").toLowerCase().includes(query) ||
      String(member.memberId || "").toLowerCase().includes(query)
    ))
  }, [memberSearch, members])
  const memberDirectoryCount = membersLoaded
    ? members.length
    : Math.max(Number(group?.subscriptions) || 0, members.length)

  const handleBannerEditFinish = (newBannerImageUrl, bannerFraming) => {
    if (!newBannerImageUrl) return
    setBannerImageUrl(newBannerImageUrl)
    setGroup(previous => ({ ...previous, bannerFraming }))
    setBannerModalVisible(false)
  }

  const handleOpenApplications = (positionId) => {
    setSelectedPositionId(positionId)
    // Fetch applications for the selected position
    // This assumes that usePositionApplications will fetch the applications when selectedPositionId changes
  }

  const handleCloseApplications = () => {
    setSelectedPositionId(null)
  }

  const fetchPayrollObligations = useCallback(async () => {
    if (!jobsPayrollEnabled || !groupId) {
      return
    }

    setPayrollLoading(true)
    setPayrollError("")

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(
        `${apiUrl}/api/Groups/${groupId}/btc-payment-intents/job-payroll/obligations?windowDays=14&autopaymentCountThreshold=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (!response.ok) {
        throw new Error("Could not load payroll obligations.")
      }

      const data = await response.json()
      setPayrollObligations(data)
    } catch (error) {
      setPayrollError(error.message || "Could not load payroll obligations.")
    } finally {
      setPayrollLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, groupId, jobsPayrollEnabled])

  useEffect(() => {
    if (activeTab === "positions") {
      fetchPayrollObligations()
    }
  }, [activeTab, fetchPayrollObligations])

  const fetchChurchInsights = useCallback(async () => {
    if (!isChurchGroup(group) || !groupId) {
      return
    }

    setChurchInsightsLoading(true)
    setChurchInsightsError("")

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(`${apiUrl}/api/GroupsTbls/${groupId}/church-network-insights`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Could not load church network insights.")
      }

      const data = await response.json()
      setChurchInsights(data)
    } catch (error) {
      setChurchInsightsError(error.message || "Could not load church network insights.")
    } finally {
      setChurchInsightsLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, group, groupId])

  useEffect(() => {
    if (activeTab === "church") {
      fetchChurchInsights()
    }
  }, [activeTab, fetchChurchInsights])

  const renderEnrollmentInsightCard = (insight, variant = "") => (
    <article key={insight.groupId} className={`group-church-insight-card ${variant}`}>
      <div className="group-church-card-topline">
        <span className="group-section-kicker">
          <Network className="h-4 w-4" />
          {insight.isCurrentGroup ? "Church body" : (insight.relationshipType || "Affiliation")}
        </span>
        {insight.authorityScope && <span className="group-soft-badge">{insight.authorityScope}</span>}
      </div>
      <h4>{insight.groupName}</h4>
      {insight.denominationOrTradition && <p>{insight.denominationOrTradition}</p>}
      <div className="group-church-stat-grid">
        <div>
          <span>Enrolled</span>
          <strong>{insight.enrolledMembers || 0}</strong>
        </div>
        <div>
          <span>Estimated body</span>
          <strong>{insight.memberCountEstimate || "TBD"}</strong>
        </div>
        <div>
          <span>Enrollment</span>
          <strong>{formatEnrollmentPercent(insight.enrollmentPercent)}</strong>
        </div>
        <div>
          <span>Churches represented</span>
          <strong>{insight.congregationCount || 1}</strong>
        </div>
      </div>
    </article>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case "forums":
        return <GroupForums groupId={groupId} isDarkMode={isDarkMode} />
      case "problems":
        return (
          <div className="group-tab-stack">
            <FetchProblems groupId={groupId} refreshKey={problemRefreshKey} />
            <PostProblem groupId={groupId} onProblemCreated={() => {
              setProblemRefreshKey((value) => value + 1)
              setWorkspaceRefreshKey((value) => value + 1)
            }} />
            <div className="group-nested-panel mt-8">
              <div className="group-section-heading">
                <span className="group-section-kicker"><Gavel className="h-4 w-4" /> Resolution</span>
                <h3>Conflicts</h3>
              </div>
              <FetchConflicts groupId={groupId} />
              <PostConflict groupId={groupId} />
            </div>
          </div>
        )
      case "ideas":
        return (
          <div>
            <FetchCampaigns groupId={groupId} groupName={group?.groupName} selectedCampaignId={requestedCampaignId} />
            <PostCampaign groupId={group?.id} />
          </div>
        )
      case "refining":
        return (
          <div>
            <RefinementList groupId={groupId} />
          </div>
        )
      case "actions":
        return (
          <div>
            <FetchActions groupId={groupId} selectedActionId={requestedActionId} />
          </div>
        )
      case "taskboard":
        return (
          <div>
            <TaskBoard groupId={groupId} group={group} />
          </div>
        )
      case "calendar":
        return (
          <div>
            <GroupCalendar groupId={groupId} group={group} refreshKey={calendarRefreshKey} selectedEventId={requestedCalendarEventId} />
          </div>
        )
      case "roles":
        return (
          <div className="group-tab-stack">
            <div className="group-section-heading">
              <span className="group-section-kicker"><BadgeCheck className="h-4 w-4" /> Official responsibilities</span>
              <h3>Roles in this community</h3>
              <p>Roles identify people who have been formally entrusted with a responsibility, such as Pastor or Treasurer. Connecting a church does not automatically assign a role.</p>
            </div>
            {rolesLoading ? (
              <StatusMessage tone="loading" title="Loading roles" message="Checking this community's official assignments." />
            ) : rolesError ? (
              <StatusMessage tone="error" title="Could not load roles" message={rolesError} actionLabel="Retry" onAction={fetchGroupRoles} />
            ) : groupRoles.length ? (
              <div className="group-role-directory">
                {groupRoles.map((role) => {
                  const assigned = String(role.status).toLowerCase() === "filled" && role.memberId
                  return <article key={role.positionId} className="group-role-card">
                    <div>
                      <h4>{role.title}</h4>
                      {role.roleNames?.length ? <p>{role.roleNames.join(" · ")}</p> : null}
                    </div>
                    <span className={assigned ? "is-assigned" : "is-open"}>
                      {assigned ? (role.memberName || "Assigned member") : (role.status || "Open")}
                    </span>
                  </article>
                })}
              </div>
            ) : (
              <StatusMessage tone="empty" title="No official roles yet" message="This community has not formally assigned any roles yet." />
            )}
          </div>
        )
      case "church":
        return (
          <div className="group-tab-stack">
            <ChurchInvitePanel group={group} />
            {groupFinanceEnabled && <a href={`/groups/${groupId}/finance`}>Giving &amp; receipts</a>}

            {!isChurchGroup(group) ? (
              <StatusMessage
                tone="empty"
                title="Fellowship invite dashboard"
                message="Use the invite flow above to connect peer churches into this denomination or fellowship network."
              />
            ) : churchInsightsLoading ? (
              <StatusMessage tone="loading" title="Loading church network" message="Checking congregation enrollment and affiliation visibility." />
            ) : churchInsightsError ? (
              <StatusMessage tone="error" title="Could not load church insights" message={churchInsightsError} actionLabel="Retry" onAction={fetchChurchInsights} />
            ) : churchInsights ? (
              <>
                <section className="group-church-dashboard">
                  <div className="group-section-heading">
                    <span className="group-section-kicker"><Network className="h-4 w-4" /> Church network</span>
                    <h3>Church and denomination visibility</h3>
                    <p>
                      Members of this church can see how much of the congregation is enrolled and how this body connects into wider affiliations.
                    </p>
                  </div>
                  {renderEnrollmentInsightCard(churchInsights.church, "is-primary")}
                </section>

                <section className="group-church-affiliations">
                  <div className="group-section-heading">
                    <span className="group-section-kicker"><BadgeCheck className="h-4 w-4" /> Affiliation dashboard</span>
                    <h3>Denomination and fellowship networks</h3>
                    <p>
                      This gives members visibility into the wider church family while keeping local churches as peers within the same tradition.
                    </p>
                  </div>
                  {churchInsights.affiliations?.length ? (
                    <div className="group-church-affiliation-grid">
                      {churchInsights.affiliations.map((insight) => renderEnrollmentInsightCard(insight))}
                    </div>
                  ) : (
                    <StatusMessage
                      tone="empty"
                      title="No affiliations connected yet"
                      message="This church is not connected to a denomination or fellowship network in TYK yet."
                    />
                  )}
                </section>
              </>
            ) : (
              <StatusMessage tone="empty" title="Church insights not loaded" message="Open this tab again to refresh church network visibility." />
            )}
          </div>
        )
      case "positions":
        return (
          <div className="group-tab-stack">
            {positionsLoading ? (
              <StatusMessage tone="loading" title="Loading positions" message="Checking open and filled roles for this group." />
            ) : positionsError ? (
              <StatusMessage tone="error" title="Could not load positions" message={positionsError} />
            ) : (
              <>
                <div className="group-section-heading">
                  <span className="group-section-kicker"><BriefcaseBusiness className="h-4 w-4" /> Role desk</span>
                  <h3>Open Positions</h3>
                </div>
                <div className="group-position-grid">
                  {positions
                    .filter((position) => !position.isFilled)
                    .map((position) => (
                      <article key={position.id} className="group-position-card">
                        <div
                          onClick={(e) => {
                            e.preventDefault()
                            setSelectedPosition(position)
                          }}
                          className="group-position-title"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault()
                              setSelectedPosition(position)
                            }
                          }}
                        >
                          {position.title}
                        </div>
                        <button
                          onClick={() => handleOpenApplications(position.id)}
                          className="group-action-button"
                        >
                          Applications ({position.applicationCount})
                        </button>
                      </article>
                    ))}
                  {positions.filter((position) => !position.isFilled).length === 0 && (
                    <div className="group-empty-state">No open positions right now.</div>
                  )}
                </div>
                <div className="group-section-heading mt-8">
                  <span className="group-section-kicker"><BadgeCheck className="h-4 w-4" /> Assigned</span>
                  <h3>Filled Positions</h3>
                </div>
                <div className="group-filled-roles">
                  {positions
                    .filter((position) => position.isFilled)
                    .map((position) => (
                      <span key={position.id}>{position.title}</span>
                    ))}
                  {positions.filter((position) => position.isFilled).length === 0 && (
                    <div className="group-empty-state">No filled positions yet.</div>
                  )}
                </div>

                <section className="group-nested-panel mt-8">
                  <div className="group-section-heading">
                    <div>
                      <span className="group-section-kicker"><BadgeCheck className="h-4 w-4" /> Payroll visibility</span>
                      <h3>Job payroll</h3>
                      <p>
                        Scheduled job payroll, wallet readiness, and recovery exposure for this group.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={fetchPayrollObligations}
                      className="group-action-button"
                    >
                      Refresh payroll
                    </button>
                  </div>

                  {payrollLoading ? (
                    <StatusMessage
                      tone="loading"
                      title="Loading payroll"
                      message="We are checking scheduled payroll obligations."
                    />
                  ) : payrollError ? (
                    <StatusMessage
                      tone="error"
                      title="Could not load payroll"
                      message={payrollError}
                      actionLabel="Retry"
                      onAction={fetchPayrollObligations}
                    />
                  ) : payrollObligations ? (
                    <div>
                      <div className="group-position-grid">
                        <div className="group-position-card">
                          <span>Due and ready</span>
                          <strong>{formatBtcAmount(payrollObligations.duePayrollReadyToPayAmount)}</strong>
                          <small>{payrollObligations.duePayrollReadyToPayCount || 0} item(s)</small>
                        </div>
                        <div className="group-position-card">
                          <span>Ready to pay</span>
                          <strong>{formatBtcAmount(payrollObligations.payrollReadyToPayAmount)}</strong>
                          <small>{payrollObligations.payrollReadyToPayCount || 0} item(s)</small>
                        </div>
                        <div className="group-position-card">
                          <span>Needs attention</span>
                          <strong>{formatBtcAmount(payrollObligations.payrollRequiringAttentionAmount)}</strong>
                          <small>{payrollObligations.payrollRequiringAttentionCount || 0} item(s)</small>
                        </div>
                        <div className="group-position-card">
                          <span>Completed recently</span>
                          <strong>{formatBtcAmount(payrollObligations.recentCompletedPayrollAmount)}</strong>
                          <small>{payrollObligations.recentCompletedPayrollCount || 0} item(s)</small>
                        </div>
                      </div>

                      {Array.isArray(payrollObligations.warnings) && payrollObligations.warnings.length > 0 && (
                        <div className="group-empty-state mt-4">
                          <strong>Payroll needs attention</strong>
                          <ul>
                            {payrollObligations.warnings.map((warning) => (
                              <li key={warning}>
                                <strong>{getPayrollWarningLabel(warning)}:</strong>{" "}
                                {getPayrollWarningAction(warning)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(payrollObligations.scheduledPayrollItems) && payrollObligations.scheduledPayrollItems.length > 0 ? (
                        <div className="legacy-table-wrap mt-5">
                          <table>
                            <thead>
                              <tr>
                                <th>Position</th>
                                <th>Amount</th>
                                <th>Scheduled</th>
                                <th>Payroll</th>
                                <th>Wallet</th>
                                <th>Intent</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payrollObligations.scheduledPayrollItems.map((item) => (
                                <tr key={item.payrollItemId}>
                                  <td>#{item.positionId}</td>
                                  <td>{formatBtcAmount(item.amount)}</td>
                                  <td>{formatDateTime(item.scheduledPaymentDate)}</td>
                                  <td>{item.payrollStatus}{item.isDue ? " due" : ""}</td>
                                  <td>{item.recipientWalletStatus}</td>
                                  <td>
                                    <div>{item.paymentIntentStatus || "Not queued"}</div>
                                    {Array.isArray(item.warnings) && item.warnings.length > 0 && (
                                      <div className="payroll-warning-list">
                                        {item.warnings.map((warning) => (
                                          <div key={warning}>
                                            <strong>{getPayrollWarningLabel(warning)}:</strong>{" "}
                                            {getPayrollWarningAction(warning)}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <StatusMessage
                          tone="empty"
                          title="No scheduled payroll"
                          message="This group has no visible payroll obligations in the current window."
                        />
                      )}
                    </div>
                  ) : null}
                </section>
              </>
            )}
          </div>
        )
      case "policies":
        return <GroupPolicies groupId={groupId} />
      default:
        return null
    }
  }

  return (
    <div className={`group-members-page ${isDarkMode ? "dark" : ""}`}>
      <div className="group-members-shell">
        <div className="group-members-container">
          {group && !isGeographicGroup(group) && <TreasurerNotice groupId={group.id || group.Id} />}
          {String(group?.groupType || group?.GroupType || '').toLowerCase() === 'fellowship' && <p className="fellowship-membership-explainer">This is your shared fellowship workspace. Members can propose ideas and vote on shared fellowship work. Your church membership stays unchanged; hosting gives no extra votes or authority over other churches.</p>}
          {loading && !group && (
            <StatusMessage
              tone="loading"
              title="Loading community"
              message="We are loading the latest group details."
            />
          )}
          {loadError && !group && (
            <StatusMessage
              tone="error"
              title="Could not load community"
              message={loadError}
              actionLabel="Retry"
              onAction={fetchGroupData}
            />
          )}
          {!loading && !loadError && !group && (
            <StatusMessage
              tone="empty"
              title="Community not found"
              message="This community could not be found or is not available to this account."
            />
          )}
          {!loadError && group && isGeographicGroup(group) && (
            <StatusMessage
              tone="info"
              title="Your local coordination space"
              message={`Membership in ${group?.groupName || "this location group"} follows your profile address, so it is not manually joinable or leaveable. Use this space to meet nearby members, share local needs, develop ideas, and coordinate action. Update your address in your profile or dashboard if your location changes.`}
            />
          )}
          {!loadError && group && (
            <>
          <section className="group-hero" style={{ background: parseFraming(group.bannerFraming).background }}>
            <img
              src={bannerImageUrl || "/placeholder.svg"}
              alt="Group Banner"
              className="group-hero-image"
              style={bannerImageStyle(group.bannerFraming)}
            />
            <div className="group-hero-overlay" />
            <div className="group-hero-content">
              <div>
                <span className="group-hero-kicker"><ShieldCheck className="h-4 w-4" /> {isGeographicGroup(group) ? "Local coordination" : "Member workspace"}</span>
                <h1>{group?.groupName}</h1>
                <p>{isGeographicGroup(group)
                  ? "Connect with believers in your area, make local needs visible, and move together in practical service."
                  : "Coordinate conversation, needs, actions, roles, policies, and shared work in one focused place."}</p>
              </div>
            </div>
            {!isGeographicGroup(group) && group?.canEditBanner && <button
              className="group-edit-banner-button"
              onClick={handleOpenModal}
            >
              <Pencil className="h-4 w-4" /> Edit Banner
            </button>}
          </section>

          <section className="group-command-bar">
            {groupFinanceEnabled && !isGeographicGroup(group) && <a href={`/groups/${groupId}/finance`}>Giving &amp; receipts</a>}
            {!isGeographicGroup(group) && <FellowshipMeetLauncher key={groupId} group={group} onCreated={() => setCalendarRefreshKey(value => value + 1)} />}
            <div>
              <span className="group-section-kicker"><FileText className="h-4 w-4" /> Active group</span>
              <h2>{loading ? "Loading..." : group?.groupName}</h2>
            </div>
            {campaignVotingEnabled && <div className="group-command-action"><PostCampaign groupId={group?.id} /></div>}
          </section>

          <FellowshipPanel group={group} />

          {bitcoinTransactionsEnabled && (
            <GroupBtcStatement groupId={groupId} />
          )}

          <section className="group-member-directory">
            <div className="group-member-directory-header">
              <div>
                <span className="group-section-kicker"><Users className="h-4 w-4" /> Members</span>
                <h2>{memberDirectoryCount} members</h2>
                <p>Browse the people participating in this group.</p>
              </div>
              <label className="group-member-search">
                <Search className="h-4 w-4" aria-hidden="true" />
                <input
                  type="search"
                  value={memberSearch}
                  onChange={(event) => setMemberSearch(event.target.value)}
                  placeholder="Search members"
                  aria-label="Search group members"
                />
              </label>
            </div>
            {membersLoading ? (
              <div className="group-member-directory-state">Loading members...</div>
            ) : membersError ? (
              <div className="group-member-directory-state is-warning">{membersError}</div>
            ) : visibleMembers.length === 0 ? (
              <div className="group-member-directory-state">
                {memberSearch ? "No members match that search." : "No member profiles are available yet."}
              </div>
            ) : (
              <div className="group-member-list" aria-label="Group member directory">
                {visibleMembers.map((member) => (
                  <article key={member.memberId} className="group-member-card">
                    {member.profilePictureUrl ? (
                      <img src={member.profilePictureUrl} alt="" className="group-member-avatar" />
                    ) : (
                      <div className="group-member-avatar" aria-hidden="true">
                        {String(member.displayName || "M").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3>{member.displayName || "Member"}</h3>
                      <p>Joined {member.joinedOn ? new Date(member.joinedOn).toLocaleDateString() : "recently"}</p>
                    </div>
                    <div className="group-member-slugs">
                      {member.isFounder && !isChurchGroup(group) && <span className="group-member-founder">Founder</span>}
                      {(rolesByMember[member.memberId] || []).map(role => (
                        <span key={role} className="group-member-role">{role}</span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="group-workspace-card">
            <div className="group-tabs-shell">
              <div className="group-tabs group-tabs-main" aria-label="Group member tabs">
                {mainTabs.map((tab) => (
                  (() => {
                    const Icon = TAB_META[tab]?.icon || FileText
                    return (
                  <button
                    key={tab}
                    className={activeTab === tab ? "group-tab is-active" : "group-tab"}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                  >
                    <Icon className="h-4 w-4" />
                    {formatTabLabel(tab)}
                    {Object.prototype.hasOwnProperty.call(workspaceCounts, tab) && (
                      <span className="group-tab-count" aria-label={`${workspaceCounts[tab]} active`}>{workspaceCounts[tab]}</span>
                    )}
                  </button>
                    )
                  })()
                ))}
              </div>

              <div className="group-tabs group-tabs-governance" aria-label="Group coordination tabs">
                {governanceTabs.map((tab) => (
                  (() => {
                    const Icon = TAB_META[tab]?.icon || FileText
                    return (
                  <button
                    key={tab}
                    className={activeTab === tab ? "group-tab group-tab-governance is-active" : "group-tab group-tab-governance"}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                  >
                    <Icon className="h-4 w-4" />
                    {formatTabLabel(tab)}
                  </button>
                    )
                  })()
                ))}
              </div>
            </div>
            <div className="group-tab-content">{renderTabContent()}</div>
          </section>
        {!isGeographicGroup(group) && group?.canEditBanner && <GroupBannerEditModal
          imageUrl={bannerImageUrl}
          framing={group?.bannerFraming}
          groupId={groupId}
          onFinish={handleBannerEditFinish}
          onCancel={handleCloseModal}
          open={modalVisible}
        />}
        {selectedPosition && (
          <JobModal job={selectedPosition} isOpen={!!selectedPosition} onClose={() => setSelectedPosition(null)} />
        )}
        {selectedPositionId && (
          <ApplicationsModal
            position={positions.find((p) => p.id === selectedPositionId)}
            applications={applications}
            loading={applicationsLoading}
            error={applicationsError}
            isOpen={!!selectedPositionId}
            onClose={handleCloseApplications}
          />
        )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default GroupMembersPage

